// Cover, contents, section dividers and the print spec.
//
// Browser-side twin of HG-CC's `scripts/collateral/lib/activation-kit.mjs`.
// Three documents share these pages — the activation pack, the poster book and
// the sticker book — so everything here takes its copy as a parameter rather
// than hard-coding the pack's words.

import { qrForSize } from "./qr";
import {
  A4,
  TONES,
  brandLockup,
  escapeHtml,
  font,
  isOnColour,
  palette,
  poweredBy,
  type PackTone,
} from "./tokens";
import type { PackPagePart } from "./physical";
import type { PackKit } from "@/types/activation/packKit";

/** One row of the contents table, reused by the print spec. */
export interface ContentsRow {
  page: string;
  what: string;
  detail: string;
  size: string;
  stock: string;
  /** False for reference pages that a printer does not quote. */
  spec: boolean;
}

export interface CoverCopy {
  /** Small caps line above the headline: "Activation pack", "Poster book". */
  eyebrow: string;
  /** Allows <br> for the line breaks the layout was drawn around. */
  headline: string;
  lede: string;
  /** Cover is always page 1 in every document we build, but not assumed. */
  n?: number;
}

export async function coverPage(kit: PackKit, copy: CoverCopy): Promise<PackPagePart> {
  const n = copy.n ?? 1;
  const q = await qrForSize(kit.url, 46, { ink: kit.brand.inkDeep, label: "cover" });
  const css = `
    #p${n} { background: ${palette.inkDeep}; color: #FFFFFF; padding: 26mm 20mm 18mm;
      display: flex; flex-direction: column; }
    #p${n} .accent { position: absolute; left: 0; top: 0; width: ${A4.w}mm; height: 5mm; background: ${palette.secondary}; }
    #p${n} .lockup { margin-bottom: 26mm; }
    #p${n} .eyebrow { font-size: 3.6mm; font-weight: 700; letter-spacing: 0.26em;
      text-transform: uppercase; color: ${palette.primary}; margin-bottom: 6mm; }
    #p${n} h1 { font-family: ${font.display}; font-size: 21mm; line-height: 1.02; font-weight: 800;
      margin: 0 0 9mm; letter-spacing: -0.015em; }
    #p${n} .who { font-family: ${font.display}; font-size: 8mm; font-weight: 700;
      color: ${palette.primary}; margin-bottom: 10mm; line-height: 1.15; }
    #p${n} .lede { font-size: 5mm; line-height: 1.45; color: rgba(255,255,255,0.85);
      max-width: 148mm; margin: 0 0 14mm; }
    /* flex: 0 0 auto or the column shrinks a 1.4mm rule to nothing. */
    #p${n} .rule { flex: 0 0 auto; width: 34mm; height: 1.4mm;
      background: ${palette.primary}; margin-bottom: 9mm; }
    #p${n} .qrrow { display: flex; align-items: center; gap: 8mm; margin-top: auto; }
    #p${n} .qrcard { background: #FFFFFF; border-radius: 4mm; padding: 5mm; flex: 0 0 auto; }
    #p${n} .qrcard svg { width: 46mm; height: 46mm; }
    #p${n} .qrcopy .l1 { font-family: ${font.display}; font-weight: 700; font-size: 6.4mm;
      color: ${palette.highlight}; margin-bottom: 2.5mm; }
    /* break-word, not break-all: the URL is hyphenated, so it wraps at the
       hyphens instead of splitting a word mid letter. */
    #p${n} .qrcopy .l2 { font-size: 3.8mm; color: rgba(255,255,255,0.7); line-height: 1.4;
      max-width: 92mm; overflow-wrap: break-word; }
    #p${n} .signature { position: absolute; left: 20mm; bottom: 10mm; }
  `;
  const html = `<div class="sheet" id="p${n}">
    <div class="accent"></div>
    ${brandLockup(kit.brand, 14, "dark")}
    <div class="eyebrow">${escapeHtml(copy.eyebrow)}</div>
    <h1>${copy.headline}</h1>
    <div class="rule"></div>
    <div class="who">${escapeHtml(kit.name)}</div>
    <p class="lede">${escapeHtml(copy.lede)}</p>
    <div class="qrrow">
      <div class="qrcard">${q.svg}</div>
      <div class="qrcopy">
        <div class="l1">Check it works</div>
        <div class="l2">${escapeHtml(kit.url)}</div>
      </div>
    </div>
    <div class="signature">${poweredBy("dark")}</div>
  </div>`;
  return { html, css, qr: { label: "cover", sizeMm: 46, qr: q } };
}

export interface ContentsCopy {
  title: string;
  lede: string;
  rows: ContentsRow[];
  /** Optional panel under the table: "Where to put it" and its rows. */
  panelTitle?: string;
  panelItems?: [string, string][];
  /** Three short columns at the foot. */
  notes?: [string, string][];
  /**
   * Two columns for a book with twenty-odd pieces. A single column cannot
   * carry that many rows and a panel underneath on one page.
   */
  columns?: 1 | 2;
}

export function contentsPage(copy: ContentsCopy, n: number): PackPagePart {
  const columns = copy.columns ?? 1;

  const row = (r: ContentsRow) => `<tr>
      <td class="pg">${escapeHtml(r.page)}</td>
      <td class="what"><b>${escapeHtml(r.what)}</b> <em>${escapeHtml(r.detail)}</em></td>
      ${columns === 1 ? `<td class="stock">${escapeHtml(r.stock)}</td>` : ""}
    </tr>`;

  const table = (rows: ContentsRow[]) => `<table>
      <tr><th>Page</th><th>What it is</th>${columns === 1 ? "<th>Print it on</th>" : ""}</tr>
      ${rows.map(row).join("")}
    </table>`;

  const half = Math.ceil(copy.rows.length / 2);
  const items =
    columns === 2
      ? `<div class="twocol">${table(copy.rows.slice(0, half))}${table(copy.rows.slice(half))}</div>`
      : table(copy.rows);

  const panel = (copy.panelItems ?? [])
    .map((p) => `<li><b>${escapeHtml(p[0])}</b><span>${escapeHtml(p[1])}</span></li>`)
    .join("");
  const noteCols = (copy.notes ?? [])
    .map((x) => `<div><b>${escapeHtml(x[0])}</b><p>${escapeHtml(x[1])}</p></div>`)
    .join("");

  const css = `
    #p${n} { padding: 15mm 18mm 12mm; display: flex; flex-direction: column; }
    #p${n} .accent { position: absolute; left: 0; top: 0; width: ${A4.w}mm; height: 3.5mm; background: ${palette.primary}; }
    #p${n} h1 { font-family: ${font.display}; font-size: 10.5mm; font-weight: 800;
      color: ${palette.ink}; margin: 0 0 2mm; }
    #p${n} .lede { font-size: 3.9mm; color: ${palette.mutedFg}; margin: 0 0 6mm; max-width: 168mm; line-height: 1.4; }
    #p${n} table { width: 100%; border-collapse: collapse; }
    #p${n} .twocol { display: flex; gap: 8mm; align-items: flex-start; }
    #p${n} .twocol table { flex: 1 1 0; min-width: 0; }
    #p${n} .twocol td.what { font-size: 3.4mm; }
    #p${n} .twocol td.what em { display: block; font-size: 2.9mm; line-height: 1.25; }
    #p${n} .twocol td.pg { width: 13mm; }
    #p${n} th { text-align: left; font-size: 3mm; letter-spacing: 0.14em; text-transform: uppercase;
      color: ${palette.mutedFg}; font-weight: 700; padding: 0 0 2.4mm; border-bottom: 0.4mm solid ${palette.border}; }
    #p${n} td { padding: 1.5mm 0; border-bottom: 0.3mm solid ${palette.borderLight}; vertical-align: baseline; }
    #p${n} td.pg { width: 16mm; font-family: ${font.display}; font-weight: 800;
      font-size: 4.2mm; color: ${palette.primary}; }
    #p${n} td.what { font-size: 3.6mm; color: ${palette.ink}; }
    #p${n} td.what b { font-weight: 600; }
    #p${n} td.what em { font-style: normal; color: ${palette.mutedFg}; font-size: 3.2mm; }
    #p${n} td.stock { width: 48mm; font-size: 3.1mm; color: ${palette.mutedFg}; line-height: 1.3; }
    #p${n} .where { margin-top: 6mm; background: ${palette.pageTint}; border: 0.3mm solid ${palette.border};
      border-radius: 3mm; padding: 5mm 7mm; }
    #p${n} .where h2 { font-family: ${font.display}; font-size: 5mm; font-weight: 700;
      color: ${palette.ink}; margin: 0 0 3mm; }
    #p${n} .where ul { list-style: none; margin: 0; padding: 0; }
    #p${n} .where li { display: flex; gap: 4mm; padding: 1mm 0; font-size: 3.3mm; }
    #p${n} .where li b { flex: 0 0 42mm; color: ${palette.ink}; font-weight: 600; }
    #p${n} .where li span { color: ${palette.mutedFg}; line-height: 1.3; }
    #p${n} .howto { margin-top: 5mm; display: flex; gap: 6mm; }
    #p${n} .howto div { flex: 1; border-top: 1mm solid ${palette.secondary}; padding-top: 3mm; }
    #p${n} .howto b { display: block; font-size: 3.4mm; color: ${palette.ink}; margin-bottom: 1.4mm; }
    #p${n} .howto p { margin: 0; font-size: 3mm; color: ${palette.mutedFg}; line-height: 1.32; }
  `;

  const html = `<div class="sheet" id="p${n}">
    <div class="accent"></div>
    <h1>${escapeHtml(copy.title)}</h1>
    <p class="lede">${escapeHtml(copy.lede)}</p>
    ${items}
    ${panel ? `<div class="where"><h2>${escapeHtml(copy.panelTitle ?? "")}</h2><ul>${panel}</ul></div>` : ""}
    ${noteCols ? `<div class="howto">${noteCols}</div>` : ""}
    <div class="pagenum">${n}</div>
  </div>`;

  return { html, css };
}

export function dividerPage(
  kit: PackKit,
  title: string,
  sub: string,
  n: number,
  tone: PackTone = "primary",
): PackPagePart {
  const t = TONES[tone];
  const onDark = isOnColour(tone);
  const css = `
    #p${n} { background: ${t.bg}; color: ${t.fg};
      display: flex; flex-direction: column; justify-content: center; padding: 0 24mm; }
    #p${n} .lockup { position: absolute; left: 24mm; top: 22mm; }
    #p${n} .num { font-family: ${font.display}; font-size: 4mm; font-weight: 700;
      color: ${onDark ? "rgba(255,255,255,0.55)" : "rgba(26,44,79,0.55)"};
      margin-bottom: 4mm; letter-spacing: 0.24em; }
    #p${n} h1 { font-family: ${font.display}; font-size: 22mm; line-height: 1;
      font-weight: 800; margin: 0 0 7mm; letter-spacing: -0.02em; }
    #p${n} p { font-size: 5mm; line-height: 1.45; margin: 0; max-width: 140mm;
      color: ${onDark ? "rgba(255,255,255,0.85)" : "rgba(26,44,79,0.8)"}; }
    #p${n} .rule { flex: 0 0 auto; width: 34mm; height: 1.4mm;
      background: ${onDark ? "rgba(255,255,255,0.6)" : "rgba(26,44,79,0.5)"}; margin: 9mm 0 0; }
  `;
  const html = `<div class="sheet" id="p${n}">
    ${brandLockup(kit.brand, 11, t.mark === "light" ? "light" : "dark")}
    <div class="num">SECTION</div>
    <h1>${escapeHtml(title)}</h1>
    <p>${escapeHtml(sub)}</p>
    <div class="rule"></div>
    <div class="pagenum ${onDark ? "oncolour" : ""}">${n}</div>
  </div>`;
  return { html, css };
}

export interface SpecCopy {
  title: string;
  lede: string;
  rows: ContentsRow[];
  /** Document-specific notes, appended to the three every job needs. */
  extraNotes?: string[];
}

export function specPage(kit: PackKit, copy: SpecCopy, n: number): PackPagePart {
  const rows = copy.rows.filter((r) => r.spec);

  // A book with a dozen different pieces needs a tighter table than a pack with
  // four. Same page, less air, rather than a spec that runs onto a second sheet
  // nobody staples to the job.
  const dense = rows.length > 9;

  const items = rows
    .map(
      (r) => `<tr>
      <td class="pg">${escapeHtml(r.page)}</td><td>${escapeHtml(r.what)}</td>
      <td>${escapeHtml(r.size)}</td><td>${escapeHtml(r.stock)}</td>
    </tr>`,
    )
    .join("");

  // The literal hex values, read off the resolved brand rather than off the
  // palette: the palette entries are CSS custom properties, and a printer
  // quoting the job needs a colour, not "var(--pk-primary)".
  const b = kit.brand;
  const notes = [
    "Everything is vector. QR codes are SVG paths and all type is live text, so the artwork enlarges to A3 or A2 without softening.",
    "Codes are error correction level H or Q, so a scuffed or partly covered sticker still scans.",
    b.isDefault
      ? `Colours: ${b.primary}, ${b.secondary} and ${b.inkDeep}. Supply as RGB. If you need CMYK, convert with a standard FOGRA or US Web Coated profile rather than re authoring the artwork.`
      : `Colours are ${b.name}'s own: primary ${b.primary}, secondary ${b.secondary}, dark ${b.inkDeep}. These are the printable values — where a brand colour was too light to carry white type at poster size it has been darkened on the same hue. Supply as RGB. If you need CMYK, convert with a standard FOGRA or US Web Coated profile rather than re authoring the artwork.`,
    ...(copy.extraNotes ?? []),
  ]
    .map((p) => `<p>${escapeHtml(p)}</p>`)
    .join("");

  const css = `
    #p${n} { padding: ${dense ? 15 : 18}mm 18mm 12mm; display: flex; flex-direction: column; }
    #p${n} .accent { position: absolute; left: 0; top: 0; width: ${A4.w}mm; height: 3.5mm; background: ${palette.secondary}; }
    #p${n} h1 { font-family: ${font.display}; font-size: ${dense ? 10 : 11}mm; font-weight: 800; color: ${palette.ink}; margin: 0 0 2.5mm; }
    #p${n} .lede { font-size: 3.7mm; color: ${palette.mutedFg}; margin: 0 0 ${dense ? 5 : 7}mm; max-width: 162mm; line-height: 1.4; }
    #p${n} table { width: 100%; border-collapse: collapse; margin-bottom: ${dense ? 5 : 7}mm; }
    #p${n} th { text-align: left; font-size: 2.9mm; letter-spacing: 0.14em; text-transform: uppercase;
      color: ${palette.mutedFg}; font-weight: 700; padding: 0 0 2.2mm; border-bottom: 0.4mm solid ${palette.border}; }
    #p${n} td { padding: ${dense ? 1.2 : 2}mm 3mm ${dense ? 1.2 : 2}mm 0; border-bottom: 0.3mm solid ${palette.borderLight};
      font-size: ${dense ? 3.05 : 3.2}mm; color: ${palette.bodyFg}; vertical-align: top; line-height: 1.28; }
    #p${n} td.pg { width: 15mm; font-family: ${font.display}; font-weight: 800; color: ${palette.primary}; font-size: ${dense ? 3.6 : 4}mm; }
    #p${n} h2 { font-family: ${font.display}; font-size: 5.2mm; font-weight: 700; color: ${palette.ink}; margin: 0 0 2.5mm; }
    #p${n} .notes p { font-size: ${dense ? 3.1 : 3.3}mm; color: ${palette.mutedFg}; line-height: 1.4;
      margin: 0 0 ${dense ? 2 : 2.6}mm; max-width: 168mm; }
    #p${n} .warn { margin-top: auto; background: ${palette.pageTint}; border-left: 1.4mm solid ${palette.secondary};
      border-radius: 0 3mm 3mm 0; padding: ${dense ? 4 : 5}mm 7mm; }
    #p${n} .warn b { display: block; font-size: 3.9mm; color: ${palette.ink}; margin-bottom: 1.6mm; }
    #p${n} .warn p { margin: 0; font-size: 3.2mm; color: ${palette.mutedFg}; line-height: 1.38; }
    #p${n} .url { font-family: ${font.mono}; font-size: 3.1mm; color: ${palette.ink};
      background: ${palette.rowAlt}; padding: 2mm 3mm; border-radius: 1.6mm; display: inline-block; word-break: break-all; }
  `;

  const html = `<div class="sheet" id="p${n}">
    <div class="accent"></div>
    <h1>${escapeHtml(copy.title)}</h1>
    <p class="lede">${escapeHtml(copy.lede)}</p>
    <table>
      <tr><th>Page</th><th>Piece</th><th>Finished size</th><th>Stock and finish</th></tr>
      ${items}
    </table>
    <h2>Notes for the printer</h2>
    <div class="notes">
      ${notes}
      <p>QR destination: <span class="url">${escapeHtml(kit.url)}</span></p>
    </div>
    <div class="warn">
      <b>Before you order a print run</b>
      <p>Scan every code on a proof from a phone at arm's length and confirm it lands on the right
        page. A print run cannot be recalled, and "the QR looks fine" is not a check.</p>
    </div>
    <div class="pagenum">${n}</div>
  </div>`;

  return { html, css };
}
